import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { forwardRef, Inject, Injectable } from '@angular/core';
import { IAccessToken, SessionTokenService } from '../../../session/service/session-token';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { HttpConfig } from '../config';
import { catchError, filter, finalize, map, take, tap, switchMap } from 'rxjs/operators';
import { SessionManager } from '../../../session/service/session-manager';
import { CampaignId } from '../../../module/campaign-util/shared/campaign-id';
import { Router } from '@angular/router';

@Injectable()
export class AuthTokenInterceptor implements HttpInterceptor {

  private static isRefreshing = false;
  private static refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(@Inject(forwardRef(() => SessionTokenService)) private sessionTokenService: SessionTokenService,
              @Inject(forwardRef(() => SessionManager)) private sessionManager: SessionManager,
              @Inject(forwardRef(() => CampaignId)) private campaignId: CampaignId,
              private router: Router,
              private config: HttpConfig) {
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const accessToken = this.sessionTokenService.getToken();
    if (accessToken.token && accessToken.refreshToken) {
      const authRequest = req.clone({
        headers: req.headers.set(
            this.config.authorization.headerName, this.config.authorization.tokenPrefix + ' ' + accessToken.token
        )
      });

      if (req.url.includes('session') && req.method === 'PATCH') {
        return next.handle(authRequest);
      }

      if (this.sessionTokenService.isAccessTokenExpired()) {
        return this.handle401Error(req, next);
      }

      return next.handle(authRequest).pipe(
          catchError(error => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
              return this.handle401Error(authRequest, next);
            }

            return throwError(error);
          })
      );
    } else if (accessToken.token) {
      // SSO session: token-only, no refresh capability
      if (this.sessionTokenService.isAccessTokenExpired()) {
        this.sessionTokenService.destroy();
        this.campaignId.release();
        this.sessionManager.destroy();
        return throwError(new Error('SSO token expired'));
      }

      const authRequest = req.clone({
        headers: req.headers.set(
            this.config.authorization!.headerName, this.config.authorization!.tokenPrefix + ' ' + accessToken.token
        )
      });

      return next.handle(authRequest).pipe(
          catchError(error => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
              this.sessionTokenService.destroy();
              this.campaignId.release();
              this.sessionManager.destroy();
            }
            return throwError(error);
          })
      );
    } else if (accessToken.refreshToken) {
      this.campaignId.release();
      this.sessionManager.destroy();
    }

    return next.handle(req);
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    return request.clone({
      headers: request.headers.set(this.config.authorization.headerName, this.config.authorization.tokenPrefix + ' ' + token)
    });
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<any> {
    console.log('handle401Error', AuthTokenInterceptor.isRefreshing);
    if (!AuthTokenInterceptor.isRefreshing) {
      AuthTokenInterceptor.isRefreshing = true;
      AuthTokenInterceptor.refreshTokenSubject.next(null);

      const refreshToken = this.sessionTokenService.getToken().refreshToken;
      console.log('refreshToken', refreshToken);

      if (refreshToken) {
        return this.sessionManager.refreshToken().pipe(
            catchError((error: any) => {
              AuthTokenInterceptor.isRefreshing = false;
              AuthTokenInterceptor.refreshTokenSubject.next(null);

              this.sessionTokenService.destroy();
              this.campaignId.release();
              this.sessionManager.destroy();
              return throwError(error);
            }),
            switchMap((response: any) => {
              const accessToken: IAccessToken = {
                token: response.token,
                refreshToken
              };
              this.sessionTokenService.persistWithExpiry(accessToken, response.expiresIn || 900);

              AuthTokenInterceptor.refreshTokenSubject.next(accessToken);
              AuthTokenInterceptor.isRefreshing = false;

              return next.handle(this.addTokenHeader(request, response.token));
            })
        );
      } else {
        AuthTokenInterceptor.isRefreshing = false;
        this.sessionTokenService.destroy();
        this.campaignId.release();
        this.sessionManager.destroy();
        return throwError(new Error('No refresh token'));
      }
    }

    return AuthTokenInterceptor.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap((accessToken: IAccessToken) =>
            next.handle(this.addTokenHeader(request, accessToken.token))
        )
    );
  }
}
